/**
 * ClaimRow Component
 *
 * Dense row displaying an enriched claim with intelligence fields.
 * Renders as a CSS Grid row in table layouts or as a stacked card on mobile.
 */

import React, { useCallback, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Avatar } from '@components/primitives/Avatar';
import {
  AccountTree as AccountTreeIcon,
  BlockOutlined as VoidIcon,
  DeleteOutline as DeleteIcon,
  Download as DownloadIcon,
  Edit as EditIcon,
  FindReplace as CorrectionIcon,
  Flag as FlagIcon,
  MoreVert as MoreVertIcon,
  PriorityHigh as PriorityHighIcon,
  Send as SendIcon,
  Visibility as VisibilityIcon,
} from '@mui/icons-material';
import {
  Box,
  Checkbox,
  Chip,
  CircularProgress,
  IconButton,
  ListItemIcon,
  ListItemText,
  Menu,
  MenuItem,
  Tooltip,
  Typography,
} from '@mui/material';
import { useTheme } from '@mui/material/styles';
import type { ClaimListItem } from '@store/generated/billing';

import { CLAIM_STATUS_OPTIONS } from '../../../constants/statusOptions';

import type { TableLayoutMode } from './types';
import { getGridTemplateColumns, GRID_GAP } from './types';

// ---------------------------------------------------------------------------
// Helpers
// ---------------------------------------------------------------------------

function getNameParts(fullName: string | null | undefined): { firstName: string; lastName: string } {
  if (!fullName) return { firstName: '', lastName: '' };
  const parts = fullName.trim().split(/\s+/);
  if (parts.length === 0) return { firstName: '', lastName: '' };
  if (parts.length === 1) return { firstName: parts[0], lastName: '' };
  return { firstName: parts[0], lastName: parts.slice(1).join(' ') };
}

const formatDate = (dateString: string | null | undefined): string => {
  if (!dateString) return '\u2014';
  try {
    const date = new Date(dateString);
    return date.toLocaleDateString('en-US', { month: 'short', day: 'numeric' });
  } catch {
    return '\u2014';
  }
};

const formatCents = (cents: number | null | undefined): string => {
  if (cents == null) return '\u2014';
  const dollars = cents / 100;
  return `$${dollars.toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
};

const getClaimStatusColor = (status: string): 'default' | 'success' | 'warning' | 'error' | 'info' | 'primary' => {
  const config = CLAIM_STATUS_OPTIONS.find(opt => opt.value === status);
  return config?.color ?? 'default';
};

const getClaimStatusLabel = (status: string): string => {
  const config = CLAIM_STATUS_OPTIONS.find(opt => opt.value === status);
  return config?.label ?? status;
};

// Priority → color mapping
const getPriorityColor = (priority: string | null | undefined): string => {
  switch (priority) {
    case 'urgent': return 'error.main';
    case 'action_needed': return 'warning.main';
    case 'monitor': return 'info.main';
    default: return 'text.secondary';
  }
};

// Read-only statuses where claim cannot be edited
const READ_ONLY_STATUSES = ['submitted', 'acknowledged', 'accepted', 'rejected', 'paid', 'partially_paid', 'denied', 'reversed', 'imported'];

// ---------------------------------------------------------------------------
// Props
// ---------------------------------------------------------------------------

interface ClaimRowProps {
  claim: ClaimListItem;
  layout: TableLayoutMode;
  selected?: boolean;
  onSelectChange?: (claimId: string, selected: boolean) => void;
  onRowClick?: (claim: ClaimListItem) => void;
  onSubmit?: (claimId: string) => void;
  onDelete?: (claim: ClaimListItem) => void;
  onDownloadPdf?: (claimId: string) => void;
  onResubmit?: (claim: ClaimListItem, code: '7' | '8') => void;
  isSubmitting?: boolean;
  submittingClaimId?: string | null;
  downloadingClaimIds?: Set<string>;
}

// ---------------------------------------------------------------------------
// Component
// ---------------------------------------------------------------------------

export const ClaimRow: React.FC<ClaimRowProps> = ({
  claim,
  layout,
  selected = false,
  onSelectChange,
  onRowClick,
  onSubmit,
  onDelete,
  onDownloadPdf,
  onResubmit,
  isSubmitting = false,
  submittingClaimId = null,
  downloadingClaimIds,
}) => {
  const theme = useTheme();
  const navigate = useNavigate();

  const [menuAnchor, setMenuAnchor] = useState<HTMLElement | null>(null);
  const menuOpen = Boolean(menuAnchor);

  const handleMenuOpen = useCallback((e: React.MouseEvent<HTMLElement>) => {
    e.stopPropagation();
    setMenuAnchor(e.currentTarget);
  }, []);

  const handleMenuClose = useCallback(() => {
    setMenuAnchor(null);
  }, []);

  const handleCheckboxClick = (e: React.MouseEvent) => {
    e.stopPropagation();
    onSelectChange?.(claim.claim_id, !selected);
  };

  const handleRowClick = useCallback(() => {
    onRowClick?.(claim);
  }, [claim, onRowClick]);

  // Derived data
  const patientNameParts = useMemo(() => getNameParts(claim.patient_name), [claim.patient_name]);
  const providerNameParts = useMemo(() => getNameParts(claim.provider_name), [claim.provider_name]);
  const isImported = claim.source_system !== 'jotpsych' && claim.source_system != null;
  const isReadOnly = READ_ONLY_STATUSES.includes(claim.status);
  const isCurrentlySubmitting = submittingClaimId === claim.claim_id && isSubmitting;
  const isDownloading = downloadingClaimIds?.has(claim.claim_id) ?? false;

  // Column visibility (order: Date, Provider, Patient, Payer, ...)
  const showDate = layout === 'full' || layout === 'standard' || layout === 'compact';
  const showProvider = layout === 'full' || layout === 'standard';
  const showPayer = layout === 'full';
  const showIssues = layout === 'full' || layout === 'standard';

  // Build menu items
  const menuItems = useMemo(() => {
    const items: { label: string; icon: React.ReactNode; onClick: () => void; disabled?: boolean }[] = [];

    // Submit action (only for ready claims)
    if (claim.status === 'ready' && (claim.error_count ?? 0) === 0 && onSubmit) {
      items.push({
        label: isCurrentlySubmitting ? 'Submitting...' : 'Submit',
        icon: isCurrentlySubmitting ? <CircularProgress size={16} /> : <SendIcon fontSize="small" />,
        onClick: () => { handleMenuClose(); onSubmit(claim.claim_id); },
        disabled: isCurrentlySubmitting,
      });
    }

    // Edit / View
    items.push({
      label: isReadOnly ? 'View' : 'Edit',
      icon: isReadOnly ? <VisibilityIcon fontSize="small" /> : <EditIcon fontSize="small" />,
      onClick: () => {
        handleMenuClose();
        if (isReadOnly) {
          navigate(`/billing/claim/${claim.claim_id}`);
        } else {
          navigate(`/billing/claim/${claim.claim_id}/edit`);
        }
      },
    });

    // Download PDF (only for submitted+ claims)
    if (isReadOnly && claim.status !== 'imported' && onDownloadPdf) {
      items.push({
        label: isDownloading ? 'Downloading...' : 'Download PDF',
        icon: isDownloading ? <CircularProgress size={16} /> : <DownloadIcon fontSize="small" />,
        onClick: () => { handleMenuClose(); onDownloadPdf(claim.claim_id); },
        disabled: isDownloading,
      });
    }

    // Correction / Void (only for correction-eligible claims)
    if (claim.correction_eligible && onResubmit) {
      items.push({
        label: 'Create Correction',
        icon: <CorrectionIcon fontSize="small" />,
        onClick: () => { handleMenuClose(); onResubmit(claim, '7'); },
      });
      items.push({
        label: 'Void Claim',
        icon: <VoidIcon fontSize="small" color="error" />,
        onClick: () => { handleMenuClose(); onResubmit(claim, '8'); },
      });
    }

    // Delete
    if (onDelete) {
      items.push({
        label: 'Delete',
        icon: <DeleteIcon fontSize="small" color="error" />,
        onClick: () => { handleMenuClose(); onDelete(claim); },
      });
    }

    return items;
  }, [claim, isReadOnly, isCurrentlySubmitting, isDownloading, onSubmit, onDelete, onDownloadPdf, onResubmit, handleMenuClose, navigate]);

  // Shared renderers
  const renderPriorityIcon = () => {
    switch (claim.next_action_priority) {
      case 'urgent':
        return <PriorityHighIcon sx={{ fontSize: 14, color: 'error.main', flexShrink: 0 }} />;
      case 'action_needed':
        return <FlagIcon sx={{ fontSize: 14, color: 'warning.main', flexShrink: 0 }} />;
      case 'monitor':
        return <VisibilityIcon sx={{ fontSize: 14, color: 'info.main', flexShrink: 0 }} />;
      default:
        return null;
    }
  };

  const renderNextAction = () => {
    if (!claim.next_action) return <Typography variant="body2" sx={{ color: theme.palette.text.disabled, fontSize: '0.8rem' }}>{'\u2014'}</Typography>;
    return (
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5, minWidth: 0 }}>
        {renderPriorityIcon()}
        <Typography
          variant="body2"
          sx={{
            fontSize: '0.75rem',
            color: getPriorityColor(claim.next_action_priority),
            fontWeight: claim.next_action_priority === 'urgent' ? 600 : 400,
            overflow: 'hidden',
            textOverflow: 'ellipsis',
            whiteSpace: 'nowrap',
          }}
          title={claim.next_action}
        >
          {claim.next_action}
        </Typography>
      </Box>
    );
  };

  const renderIssues = () => {
    const errorCount = claim.error_count ?? 0;
    const warningCount = claim.warning_count ?? 0;
    if (errorCount === 0 && warningCount === 0) return null;

    return (
      <Tooltip title={claim.top_blocker || ''} arrow placement="top">
        <Box sx={{ display: 'flex', gap: 0.5, alignItems: 'center' }}>
          {errorCount > 0 && (
            <Chip
              label={errorCount}
              size="small"
              sx={{
                height: 18,
                fontSize: '0.6rem',
                fontWeight: 700,
                bgcolor: `${theme.palette.error.main}15`,
                color: theme.palette.error.main,
                '& .MuiChip-label': { px: 0.5 },
              }}
            />
          )}
          {warningCount > 0 && (
            <Chip
              label={warningCount}
              size="small"
              sx={{
                height: 18,
                fontSize: '0.6rem',
                fontWeight: 700,
                bgcolor: `${theme.palette.warning.main}15`,
                color: theme.palette.warning.main,
                '& .MuiChip-label': { px: 0.5 },
              }}
            />
          )}
        </Box>
      </Tooltip>
    );
  };

  const renderActionsMenu = () => (
    <Menu
      anchorEl={menuAnchor}
      open={menuOpen}
      onClose={handleMenuClose}
      onClick={e => e.stopPropagation()}
      transformOrigin={{ horizontal: 'right', vertical: 'top' }}
      anchorOrigin={{ horizontal: 'right', vertical: 'bottom' }}
      slotProps={{
        paper: { sx: { minWidth: 160, boxShadow: theme.shadows[4] } },
      }}
    >
      {menuItems.map(item => (
        <MenuItem key={item.label} onClick={item.onClick} disabled={item.disabled} dense>
          <ListItemIcon sx={{ minWidth: '32px !important' }}>{item.icon}</ListItemIcon>
          <ListItemText primaryTypographyProps={{ fontSize: '0.8rem' }}>{item.label}</ListItemText>
        </MenuItem>
      ))}
    </Menu>
  );

  // ---- Card layout (mobile) ----

  if (layout === 'card') {
    return (
      <Box
        onClick={handleRowClick}
        sx={{
          px: 2,
          py: 1,
          borderBottom: `1px solid ${theme.palette.grey[100]}`,
          cursor: onRowClick ? 'pointer' : 'default',
          transition: 'background-color 0.15s ease',
          bgcolor: selected ? `${theme.palette.primary.main}08` : 'transparent',
          opacity: isImported ? 0.6 : 1,
          '&:hover': onRowClick ? { bgcolor: selected ? `${theme.palette.primary.main}12` : theme.palette.grey[50] } : {},
          '&:last-child': { borderBottom: 'none' },
        }}
      >
        {/* Top row: Checkbox + Patient + Status + Actions */}
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 0.75 }}>
          {onSelectChange && (
            <Checkbox checked={selected} onClick={handleCheckboxClick} size="small" sx={{ p: 0, flexShrink: 0 }} />
          )}

          {claim.patient_id && (
            <Avatar
              id={claim.patient_id}
              firstName={patientNameParts.firstName}
              lastName={patientNameParts.lastName}
              sx={{ width: 28, height: 28, fontSize: '0.65rem', fontWeight: 600, flexShrink: 0 }}
            />
          )}

          <Box sx={{ flex: 1, minWidth: 0 }}>
            <Typography variant="body2" sx={{ fontWeight: 500, fontSize: '0.8rem', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
              {claim.patient_name || 'Unknown Patient'}
            </Typography>
            {claim.payer_name && (
              <Typography variant="caption" sx={{ color: theme.palette.text.secondary, fontSize: '0.6rem', display: 'block', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                {claim.payer_name}
              </Typography>
            )}
          </Box>

          <Chip
            label={getClaimStatusLabel(claim.status)}
            size="small"
            color={getClaimStatusColor(claim.status)}
            sx={{ height: 18, fontSize: '0.65rem', fontWeight: 600, '& .MuiChip-label': { px: 0.5 }, flexShrink: 0 }}
          />

          {(claim.claim_type === 'corrected' || claim.claim_type === 'void') && (
            <Chip
              label={claim.claim_type === 'corrected' ? 'Corr' : 'Void'}
              size="small"
              sx={{ height: 16, fontSize: '0.55rem', flexShrink: 0, bgcolor: claim.claim_type === 'corrected' ? `${theme.palette.info.main}15` : `${theme.palette.error.main}15`, color: claim.claim_type === 'corrected' ? theme.palette.info.main : theme.palette.error.main, '& .MuiChip-label': { px: 0.25 } }}
            />
          )}

          {claim.has_children && (
            <Tooltip title="Has corrections or voids" arrow placement="top">
              <AccountTreeIcon sx={{ fontSize: 14, color: theme.palette.text.secondary, flexShrink: 0 }} />
            </Tooltip>
          )}

          {renderIssues()}

          {menuItems.length > 0 && (
            <IconButton size="small" onClick={handleMenuOpen} sx={{ p: 0.25, flexShrink: 0 }}>
              <MoreVertIcon sx={{ fontSize: 18 }} />
            </IconButton>
          )}
        </Box>

        {/* Provider + Date row */}
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, mb: 0.5, flexWrap: 'wrap' }}>
          {claim.provider_name && (
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
              <Avatar
                id={claim.provider_name}
                firstName={providerNameParts.firstName}
                lastName={providerNameParts.lastName}
                sx={{ width: 20, height: 20, fontSize: '0.6rem', fontWeight: 600, flexShrink: 0 }}
              />
              <Typography variant="body2" sx={{ fontSize: '0.75rem', color: theme.palette.text.secondary, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                {claim.provider_name}
              </Typography>
            </Box>
          )}

          <Typography variant="body2" sx={{ fontSize: '0.75rem', color: theme.palette.text.secondary }}>
            {formatDate(claim.service_date_from)}
          </Typography>

          <Typography variant="body2" sx={{ fontSize: '0.75rem', fontFamily: 'monospace', fontWeight: 600 }}>
            {formatCents(claim.total_charge_cents)}
          </Typography>
        </Box>

        {/* Next Action row */}
        {claim.next_action && (
          <Box sx={{ mt: 0.25 }}>
            {renderNextAction()}
          </Box>
        )}

        {renderActionsMenu()}
      </Box>
    );
  }

  // ---- Table-row layout (full / standard / compact) ----

  return (
    <Box
      onClick={handleRowClick}
      sx={{
        px: 2,
        py: 0.75,
        borderBottom: `1px solid ${theme.palette.grey[100]}`,
        cursor: onRowClick ? 'pointer' : 'default',
        transition: 'background-color 0.15s ease',
        bgcolor: selected ? `${theme.palette.primary.main}08` : 'transparent',
        opacity: isImported ? 0.6 : 1,
        '&:hover': onRowClick ? { bgcolor: selected ? `${theme.palette.primary.main}12` : theme.palette.grey[50] } : {},
        '&:last-child': { borderBottom: 'none' },
      }}
    >
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
        {onSelectChange && (
          <Checkbox checked={selected} onClick={handleCheckboxClick} size="small" sx={{ p: 0, flexShrink: 0 }} />
        )}

        <Box
          sx={{
            flex: 1,
            display: 'grid',
            gridTemplateColumns: getGridTemplateColumns(layout),
            gap: GRID_GAP,
            alignItems: 'center',
            minHeight: 32,
          }}
        >
          {/* Service Date (first column — matches encounters order) */}
          {showDate && (
            <Box sx={{ minWidth: 0 }}>
              <Typography variant="body2" sx={{ fontWeight: 500, fontSize: '0.8rem', lineHeight: 1.3 }}>
                {formatDate(claim.service_date_from)}
              </Typography>
              {claim.days_since_submission != null && (
                <Typography variant="caption" sx={{ color: theme.palette.text.secondary, fontSize: '0.65rem', lineHeight: 1 }}>
                  {claim.days_since_submission}d ago
                </Typography>
              )}
            </Box>
          )}

          {/* Provider */}
          {showProvider && (
            <Box sx={{ minWidth: 0 }}>
              {claim.provider_name ? (
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.75 }}>
                  <Avatar
                    id={claim.provider_name}
                    firstName={providerNameParts.firstName}
                    lastName={providerNameParts.lastName}
                    sx={{ width: 24, height: 24, fontSize: '0.65rem', fontWeight: 600, flexShrink: 0 }}
                  />
                  <Typography
                    variant="body2"
                    sx={{ fontWeight: 500, fontSize: '0.8rem', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}
                    title={claim.provider_name}
                  >
                    {claim.provider_name}
                  </Typography>
                </Box>
              ) : (
                <Typography variant="body2" sx={{ color: theme.palette.text.disabled, fontSize: '0.8rem' }}>{'\u2014'}</Typography>
              )}
            </Box>
          )}

          {/* Patient */}
          <Box sx={{ minWidth: 0 }}>
            {claim.patient_id ? (
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.75 }}>
                <Avatar
                  id={claim.patient_id}
                  firstName={patientNameParts.firstName}
                  lastName={patientNameParts.lastName}
                  sx={{ width: 24, height: 24, fontSize: '0.65rem', fontWeight: 600, flexShrink: 0 }}
                />
                <Box sx={{ minWidth: 0 }}>
                  <Typography
                    variant="body2"
                    sx={{ fontWeight: 500, fontSize: '0.8rem', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}
                    title={claim.patient_name || undefined}
                  >
                    {claim.patient_name || 'Unknown Patient'}
                  </Typography>
                  {showPayer && claim.payer_name && (
                    <Typography
                      variant="caption"
                      sx={{ color: theme.palette.text.secondary, fontSize: '0.6rem', lineHeight: 1, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap', display: 'block' }}
                      title={claim.payer_name}
                    >
                      {claim.payer_name}
                    </Typography>
                  )}
                </Box>
              </Box>
            ) : (
              <Typography variant="body2" sx={{ color: theme.palette.text.disabled, fontSize: '0.8rem' }}>
                {claim.patient_name || '\u2014'}
              </Typography>
            )}
          </Box>

          {/* Payer (full only — on standard+, payer shown as subtitle under patient) */}
          {showPayer && (
            <Box sx={{ minWidth: 0 }}>
              <Typography
                variant="body2"
                sx={{
                  fontSize: '0.8rem',
                  color: claim.payer_name ? theme.palette.text.secondary : theme.palette.text.disabled,
                  overflow: 'hidden',
                  textOverflow: 'ellipsis',
                  whiteSpace: 'nowrap',
                }}
                title={claim.payer_name || undefined}
              >
                {claim.payer_name || '\u2014'}
              </Typography>
            </Box>
          )}

          {/* Next Action */}
          <Box sx={{ minWidth: 0 }}>
            {renderNextAction()}
          </Box>

          {/* Amount */}
          <Box sx={{ minWidth: 0, textAlign: 'right' }}>
            <Typography
              variant="body2"
              sx={{ fontFamily: 'monospace', fontSize: '0.8rem', fontWeight: 500 }}
            >
              {formatCents(claim.total_charge_cents)}
            </Typography>
          </Box>

          {/* Status + Lineage indicators */}
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5, flexWrap: 'wrap' }}>
            <Chip
              label={getClaimStatusLabel(claim.status)}
              size="small"
              color={getClaimStatusColor(claim.status)}
              sx={{
                height: 18,
                fontSize: '0.6rem',
                fontWeight: 600,
                textTransform: 'capitalize',
                '& .MuiChip-label': { px: 0.5 },
              }}
            />
            {claim.claim_type === 'corrected' && (
              <Chip
                label="Corr"
                size="small"
                icon={<CorrectionIcon sx={{ fontSize: 12 }} />}
                sx={{ height: 16, fontSize: '0.55rem', bgcolor: `${theme.palette.info.main}15`, color: theme.palette.info.main, '& .MuiChip-label': { px: 0.25 }, '& .MuiChip-icon': { ml: 0.25, fontSize: 12 } }}
              />
            )}
            {claim.claim_type === 'void' && (
              <Chip
                label="Void"
                size="small"
                icon={<VoidIcon sx={{ fontSize: 12 }} />}
                sx={{ height: 16, fontSize: '0.55rem', bgcolor: `${theme.palette.error.main}15`, color: theme.palette.error.main, '& .MuiChip-label': { px: 0.25 }, '& .MuiChip-icon': { ml: 0.25, fontSize: 12 } }}
              />
            )}
            {claim.has_children && (
              <Tooltip title="Has corrections or voids" arrow placement="top">
                <AccountTreeIcon sx={{ fontSize: 14, color: theme.palette.text.secondary }} />
              </Tooltip>
            )}
          </Box>

          {/* Issues */}
          {showIssues && (
            <Box sx={{ display: 'flex', alignItems: 'center' }}>
              {renderIssues()}
            </Box>
          )}

          {/* Actions */}
          <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'flex-end' }}>
            {menuItems.length > 0 && (
              <IconButton size="small" onClick={handleMenuOpen} sx={{ p: 0.25 }}>
                <MoreVertIcon sx={{ fontSize: 18 }} />
              </IconButton>
            )}
          </Box>
        </Box>
      </Box>

      {renderActionsMenu()}
    </Box>
  );
};
